
/** The column's inset above the lower of the card bottom and the nav (the pre-56d `bottom-3`). */
export const CONTROL_BASE_INSET = 12;

/**
 * 56g (verifier finding) — lay out the Overview control column against the VISIBLE
 * band of the map: from the map surface's top edge down to the lower of the card's
 * bottom and the bottom nav's top, less the 12 px inset.
 *
 * The first 56g clamp bounded the lift by the surface's FULL height, which is
 * not what the eye sees: a card pushed down (an "Install Raad" prompt, the offline
 * banner, a short phone) can leave a visible band SHORTER than the column. Then the
 * clamp kept the column off the header but pushed it over the nav — onto "More",
 * the exact 56d mis-tap. Measured: band 152 px, 5-button column 232 px → 68 px over
 * the nav.
 *
 * Priority, in order (industry-standard map chrome):
 *   1. never cover the bottom nav   → bottom = 12 + overlap, always;
 *   2. never rise over the header   → if the full column does not fit the band,
 *   3. show the ESSENTIAL controls only (layers, +, −) — `compact: true`; fit-all and
 *      the zones toggle remain on the full Live map one tap away.
 * If even the essentials do not fit (a band under ~150 px), priority 1 still wins.
 */
export type ControlLevel = 'full' | 'compact' | 'minimal';

export interface ControlLayoutInput {
  /** px of the card that sits under the nav (0 when none). */
  overlap: number;
  /** viewport y of the map surface's top edge (the header's bottom). */
  surfaceTop: number;
  /** viewport y of the card's bottom edge. */
  cardBottom: number;
  /** viewport y of the nav's top edge, or +Infinity when there is no nav. */
  navTop: number;
  /** height of ONE control button and the gap between buttons. */
  buttonHeight: number;
  gap: number;
  /** how many buttons each level shows (full, then compact, then minimal). */
  counts: { full: number; compact: number; minimal: number };
}

const columnHeight = (n: number, h: number, g: number) => (n <= 0 ? 0 : n * h + (n - 1) * g);

/**
 * `full` when every control fits the visible band; else `compact` (layers, +, -);
 * else `minimal` (layers only; pinch still zooms and "Open full map" is one tap
 * away). The nav is NEVER covered: `bottom` is always 12 + overlap.
 */
export function layoutControlColumn(i: ControlLayoutInput): { bottom: number; level: ControlLevel } {
  const bottom = CONTROL_BASE_INSET + Math.max(0, i.overlap);
  if (i.buttonHeight <= 0) return { bottom, level: 'full' }; // not measured yet
  const band = Math.min(i.cardBottom, i.navTop) - CONTROL_BASE_INSET - i.surfaceTop;
  for (const level of ['full', 'compact'] as const) {
    if (columnHeight(i.counts[level], i.buttonHeight, i.gap) <= band) return { bottom, level };
  }
  return { bottom, level: 'minimal' };
}
